import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../context';
import type { Comment, EditHistoryItem } from '../types';

export function useComments(mediaId: string) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['comments', mediaId],
    queryFn: () => apiClient.get<{ comments: Comment[] }>(`/api/media/${mediaId}/comments`),
    refetchOnWindowFocus: true,
  });
}

export function useAddComment(mediaId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { content: string; parentId?: string }) =>
      apiClient.post<{ comment: Comment }>(`/api/media/${mediaId}/comments`, params),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', mediaId] });
    },
  });
}

export function useDeleteComment(mediaId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => apiClient.delete(`/api/comments/${commentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', mediaId] });
    },
  });
}

export function useEditComment(mediaId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      apiClient.patch<{ comment: Comment }>(`/api/comments/${commentId}`, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', mediaId] });
    },
  });
}

export function useCommentHistory(commentId: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['comment-history', commentId],
    queryFn: () => apiClient.get<{ history: EditHistoryItem[] }>(`/api/comments/${commentId}/history`),
    enabled: !!commentId,
  });
}
